import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Output,
	computed,
	effect,
	forwardRef,
	inject,
	input,
	linkedSignal,
	signal,
} from "@angular/core";
import {
	type ControlValueAccessor,
	FormsModule,
	NG_VALUE_ACCESSOR,
} from "@angular/forms";
import type { Tables } from "@app/@classes/supabase";
import { LocationsService } from "@app/@services/data/locations.service";
import { ButtonComponent } from "@app/widgets/base/button/button.component";
import { DialogService } from "@app/widgets/base/dialog/dialog.service";
import { IconComponent } from "@app/widgets/base/icon/icon.component";
import { InputComponent } from "@app/widgets/base/input/input.component";
import { TranslateModule } from "@ngx-translate/core";

const noop = () => {};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DestinationInputComponent),
	multi: true,
};

@Component({
	selector: "cb-destination-input",
	imports: [
		TranslateModule,
		InputComponent,
		ButtonComponent,
		IconComponent,
		FormsModule,
	],
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	templateUrl: "./destination-input.component.html",
	styleUrl: "./destination-input.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DestinationInputComponent implements ControlValueAccessor {
	private readonly locationsService = inject(LocationsService);
	private readonly dialogService = inject(DialogService);

	public readonly companyId = input<string>();
	public readonly locationType = input<"load" | "unload" | "any">("any");
	public readonly showLink = input<boolean>(false);

	public readonly locationData = signal<Tables<"locations"> | null>(null);

	public readonly locationName = linkedSignal<string>(() => {
		if (!this.locationData()) {
			return "";
		}

		return this.locationData()?.name || "";
	});

	/**
	 * Placeholder of the input
	 */
	public readonly placeholder = input<string>();

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input(false);

	public readonly isDisabled = linkedSignal(() => this.disabled());

	/**
	 * The internal data model
	 */
	protected value = signal<string | null>(null);

	public readonly mapUrl = computed(() => {
		if (!this.locationData()) {
			return null;
		}

		const latitude = this.locationData()?.latitude;
		const longitude = this.locationData()?.longitude;
		const address = this.locationData()?.address;

		if (latitude && longitude) {
			const coordinates = [latitude, longitude];
			return `https://www.google.com/maps/search/?api=1&query=${coordinates.join(
				",",
			)}`;
		}

		if (address) {
			return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
		}

		return null;
	});

	/**
	 * Emits the value of the input on change
	 */
	@Output() public change: EventEmitter<string | null> = new EventEmitter<
		string | null
	>();

	constructor() {
		effect(() => {
			this.emitChanges();
		});
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	public writeValue(obj: string | null): void {
		if (obj !== this.value()) {
			const value = obj;

			if (!value || value.length === 0) {
				this.value.set(null);
			} else {
				this.value.set(value);
				this.loadWriteValueData();
			}
		}
	}

	private loadWriteValueData(): void {
		const id = this.value();

		if (id) {
			this.locationsService.getById(id).subscribe((location) => {
				this.locationData.set(location);
			});
		} else {
			this.locationData.set(null);
		}
	}

	public async openDestinationSelection(): Promise<void> {
		if (this.isDisabled()) {
			return;
		}

		// Dynamic import to break circular dependency
		const { SelectLocationDialogComponent } = await import(
			"@app/modules/locations/dialogs/select-location-dialog/select-location-dialog.component"
		);

		this.dialogService
			.open(SelectLocationDialogComponent, {
				position: "right",
				height: "100%",
				data: {
					companyId: this.companyId(),
					locationType: this.locationType(),
					selectedLocation: this.value(),
				},
			})
			.onClose.subscribe((location) => {
				if (location) {
					this.value.set(location.id);
					this.locationData.set(location);
				}
			});
	}

	private emitChanges(): void {
		const v = this.value();

		this.onChangeCallback(v);
		this.change.emit(this.value());
	}

	public reset(): void {
		this.value.set(null);
	}

	public setDisabledState(isDisabled: boolean): void {
		this.isDisabled.set(isDisabled);
	}
}
